import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { toastify } from '../../../utils/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { powerMeterBaseUrl, getFacilitiesURL } from '../../../utils/urls';
import Layout from '../component/layout';

function PowerMeters() {
  const navigate = useNavigate();
  const [powerMeters, setPowerMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedPowerMeter, setSelectedPowerMeter] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination options
  const rowsPerPageOptions = [10, 20, 50, 100];

  // Configuration states for dropdowns 
  const [manufacturers, setManufacturers] = useState([]);
  const [customerBands, setCustomerBands] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [meterTypes, setMeterTypes] = useState([
    { _id: '2-phase', name: '2 phase' },
    { _id: '3-phase', name: '3 phase' }
  ]);
  const [liveStatusOptions, setLiveStatusOptions] = useState([
    { _id: 'on', name: 'On' },
    { _id: 'off', name: 'Off' },
    { _id: 'maintenance', name: 'Maintenance' },
    { _id: 'faulty', name: 'Faulty' }
  ]);

  // State for file upload dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Base URLs for configs 
  const manufacturerBaseUrl = '/api/core/power_meter_management/controllers/manufacturers';
  const customerBandBaseUrl = '/api/core/power_meter_management/controllers/customer_band';
  const powerMeterUrl = '/api/core/power_meter_management/controllers/power_meters';
  const facilitiesBaseUrl = getFacilitiesURL;

  // Form data state for editing
  const [formData, setFormData] = useState({
    meterSerialNumber: '',
    deviceId: '',
    gatewayId: '',
    meterReading: '',
    manufacturer: '',
    type: '',
    liveStatus: '',
    facilityId: '',
  });

  const fetchConfigurations = async () => {
    try {
      // Fetch manufacturers
      try {
        const manufacturersRes = await makeRequest2(`${manufacturerBaseUrl}/get_all`, 'GET');
        console.log('Manufacturers response:', manufacturersRes);
        
        if (manufacturersRes && (manufacturersRes.success || manufacturersRes.data)) {
          const manufacturersData = manufacturersRes.data || [];
          
          // Ensure it's an array before setting
          if (Array.isArray(manufacturersData)) {
            setManufacturers(manufacturersData);
          } else {
            console.warn('Manufacturers data is not an array:', manufacturersData);
            setManufacturers([]);
          }
        } else {
          console.log('Failed to fetch manufacturers, using fallback');
          setManufacturers([]);
        }
      } catch (manufacturersError) {
        console.error('Error fetching manufacturers:', manufacturersError);
        setManufacturers([]);
      }

      // Fetch customer bands 
      try {
        const customerBandsRes = await makeRequest2(`${customerBandBaseUrl}/get_all`, 'GET');
        console.log('Customer bands response:', customerBandsRes);
        
        if (customerBandsRes && (customerBandsRes.success || customerBandsRes.data)) {
          const customerBandsData = customerBandsRes.data || [];
          
          // Ensure it's an array before setting
          if (Array.isArray(customerBandsData)) {
            setCustomerBands(customerBandsData);
          } else {
            console.warn('Customer bands data is not an array:', customerBandsData);
            setCustomerBands([]);
          }
        } else {
          setCustomerBands([]);
        }
      } catch (customerBandsError) {
        console.error('Error fetching customer bands:', customerBandsError);
        setCustomerBands([]);
      }

      // Fetch facilities
      try {
        const facilitiesRes = await makeRequest2(facilitiesBaseUrl, 'GET');
        console.log('Facilities response:', facilitiesRes);
        
        if (facilitiesRes && facilitiesRes.success) {
          let facilitiesData = [];
          
          if (Array.isArray(facilitiesRes.data)) {
            facilitiesData = facilitiesRes.data;
          } else if (facilitiesRes.data && Array.isArray(facilitiesRes.data.facilities)) {
            facilitiesData = facilitiesRes.data.facilities;
          } else if (facilitiesRes.facilities && Array.isArray(facilitiesRes.facilities)) {
            facilitiesData = facilitiesRes.facilities;
          }
          
          setFacilities(facilitiesData);
        } else {
          setFacilities([]);
        }
      } catch (facilitiesError) {
        console.error('Error fetching facilities:', facilitiesError);
        setFacilities([]);
      }
      
    } catch (error) {
      console.error('Error in fetchConfigurations:', error);
      
      // Set fallback empty arrays to prevent map errors
      setManufacturers([]);
      setCustomerBands([]);
      setFacilities([]);
    }
  };

  const fetchPowerMeters = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(`${powerMeterUrl}/get_all`, 'GET');
      console.log('Power meters response:', response);
      
      if (response && (response.success || response.data.data !== undefined)) {
        // Handle different response structures
        let powerMetersData = [];
        
        if (Array.isArray(response.data.data)) {
          powerMetersData = response.data.data;
        } else if (response.data && Array.isArray(response.data.data.powerMeters)) {
          powerMetersData = response.data.powerMeters;
        } else if (response.powerMeters && Array.isArray(response.powerMeters)) {
          powerMetersData = response.powerMeters;
        } else if (response.data.data && response.data.data.length === undefined) {
          // If data is an object but not an array, it might be empty
          powerMetersData = [];
        }
        
        // Ensure it's an array before setting
        if (Array.isArray(powerMetersData)) {
          // Sort power meters by status - On first, then others
          const sortedMeters = powerMetersData.sort((a, b) => {
            if (a.liveStatus === 'on' && b.liveStatus !== 'on') return -1;
            if (a.liveStatus !== 'on' && b.liveStatus === 'on') return 1;
            return 0;
          });
          
          setPowerMeters(sortedMeters);
        } else {
          console.warn('Power meters data is not an array:', powerMetersData);
          setPowerMeters([]);
        }
      } else {
        toastify(response?.error || 'Failed to fetch power meters', 'error');
        setPowerMeters([]);
      }
    } catch (error) {
      console.error('Error fetching power meters:', error);
      toastify('An error occurred while fetching power meters', 'error');
      setPowerMeters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchConfigurations();
      await fetchPowerMeters();
    };
    loadData();
  }, []);

  // Updated filtering logic to search by serial number, manufacturer, and gateway ID
  const filteredPowerMeters = powerMeters.filter(powerMeter => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in meter serial number
    const serialNumberMatch = powerMeter.meterSerialNumber?.toString().toLowerCase().includes(searchLower);
    
    // Search in manufacturer
    const manufacturerMatch = powerMeter.manufacturer?.toString().toLowerCase().includes(searchLower);
    
    // Search in gateway ID
    const gatewayMatch = powerMeter.gatewayId?.toString().toLowerCase().includes(searchLower);
    
    // Search in device ID
    const deviceMatch = powerMeter.deviceId?.toString().toLowerCase().includes(searchLower);
    
    return serialNumberMatch || manufacturerMatch || gatewayMatch || deviceMatch;
  });

  // Helper function to get facility name
  const getFacilityName = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    return facility ? facility.name : 'Unknown Facility';
  };

  // Templates for DataTable columns
  const facilityTemplate = (rowData) => getFacilityName(rowData.facilityId);
  const manufacturerTemplate = (rowData) => rowData.manufacturer || 'N/A';
  const typeTemplate = (rowData) => rowData.type || 'N/A';
  const meterReadingTemplate = (rowData) => `${rowData.currentMeterReading || 0} kWh`;
  const lastUpdatedTemplate = (rowData) => {
    if (!rowData.lastUpdated) return 'N/A';
    return new Date(rowData.lastUpdated).toLocaleString();
  };

  const statusTemplate = (rowData) => {
    const statusColors = {
      on: 'success',
      off: 'danger',
      maintenance: 'warning',
      faulty: 'danger'
    };

    const displayStatus = rowData.liveStatus?.charAt(0).toUpperCase() + rowData.liveStatus?.slice(1);

    return (
      <span className={`badge bg-${statusColors[rowData.liveStatus?.toLowerCase()] || 'secondary'} text-light`}>
        {displayStatus || 'Unknown'}
      </span>
    );
  };
  const liveStatusTemplate = (rowData) => {
    const statusColors = {
      online: 'success',
      offline: 'danger',
      maintenance: 'warning',
      faulty: 'danger'
    };

    const displayStatus = rowData.meterLiveStatus?.charAt(0).toUpperCase() + rowData.meterLiveStatus?.slice(1);

    return (
      <span className={`badge bg-${statusColors[rowData.meterLiveStatus?.toLowerCase()] || 'secondary'} text-light`}>
        {displayStatus || 'Unknown'}
      </span>
    );
  };

  const actionsTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info p-mr-2"
        onClick={() => handleModalOpen(rowData)}
        tooltip="Edit"
      />
    </div>
  );

  // file select for import
  const handleFileSelect = (event) => {
    setUploadedFile(event.files[0]);
  };

  // importing power meters
  const handleFileUpload = async () => {
    if (!uploadedFile) {
      toastify('Please select a file first', 'error');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('file', uploadedFile);

    try {
      const response = await makeRequest2(`${powerMeterUrl}/import`, 'POST', formDataObj, true);
      if (response.success) {
        toastify('Power meters imported successfully', 'success');
        fetchPowerMeters();
        setShowImportDialog(false);
      } else {
        toastify(response.error || 'Failed to import power meters', 'error');
      }
    } catch (error) {
      toastify('An error occurred while importing power meters', 'error');
    }
  };

  const handleModalOpen = (powerMeter) => {
    if (powerMeter) {
      setSelectedPowerMeter(powerMeter);
      setFormData({
        meterSerialNumber: powerMeter.meterSerialNumber || '',
        deviceId: powerMeter.deviceId || '',
        gatewayId: powerMeter.gatewayId || '',
        meterReading: powerMeter.meterReading || '',
        manufacturer: powerMeter.manufacturer || '',
        type: powerMeter.type || '',
        liveStatus: powerMeter.liveStatus || '',
        facilityId: powerMeter.facilityId || '',
      });
    }
    setShowModal(true);
  };

  // Handle power meter update submission
  const handleSubmit = async () => {
    if (!formData.meterSerialNumber.trim()) {
      toastify("Meter Serial Number is required", "error");
      return;
    }
    if (!formData.gatewayId.trim()) {
      toastify("Gateway ID is required", "error");
      return;
    }
    if (!formData.manufacturer) {
      toastify("Manufacturer is required", "error");
      return;
    }
    if (!formData.type) {
      toastify("Meter Type is required", "error");
      return;
    }
    if (!formData.liveStatus) {
      toastify("Status is required", "error");
      return;
    }
    
    console.log("Submitting power meter update with data:", formData);

    setIsSubmitting(true);
    try {
      const response = await makeRequest2(
        `${powerMeterUrl}/edit_meter/${selectedPowerMeter._id}`,
        'PUT',
        formData
      );

      if (response.success || response.message) {
        toastify('Power meter updated successfully', 'success');
        fetchPowerMeters();
        setShowModal(false);
      } else {
        toastify(response.error || 'Failed to update power meter', 'error');
      }
    } catch (error) {
      console.error('Error updating power meter:', error);
      toastify('An error occurred while updating the power meter', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Import Dialog Component
  const ImportDialog = () => (
    <Dialog
      header="Import Power Meters"
      visible={showImportDialog}
      onHide={() => setShowImportDialog(false)}
      style={{ width: '50vw' }}
    >
      <div className="p-fluid">
        <div className="field mb-4">
          <h5>Upload CSV or Excel File</h5>
          <FileUpload
            mode="basic"
            accept=".csv,.xlsx,.xls"
            maxFileSize={1000000}
            chooseLabel="Choose File"
            className="mb-3"
            onSelect={handleFileSelect}
          />
          <small className="text-muted block">
            Allowed file types: .csv, .xlsx, .xls (Max size: 1MB)
          </small>
        </div>
        <div className="text-end mt-4">
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={() => setShowImportDialog(false)}
            className="p-button-text mr-2"
          />
          <Button
            label="Import"
            icon="pi pi-check"
            onClick={handleFileUpload}
            disabled={!uploadedFile}
          />
        </div>
      </div>
    </Dialog>
  );

  return (
    <Layout>
      <ConfirmDialog />
      <div className="page-header">
        <ul className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/core/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">Power Meter Management</li>
          <li className="breadcrumb-item active">Power Meters</li>
        </ul>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-3 col-xs-12 mb-3">
                  <input
                    className="form-control"
                    placeholder="Search by serial number, manufacturer, gateway ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-9">
                  <div style={{ float: 'right' }}>
                    <button
                      className="btn btn-primary me-2"
                      onClick={() => navigate('/core/power_meter_management/add_new_power_meter')}
                    >
                      Add New Power Meter
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowImportDialog(true)}
                    >
                      Import Power Meter(s)
                    </button>
                  </div>
                </div>
              </div>

              <div className="dt-responsive table-responsive">
                <DataTable
                  value={filteredPowerMeters}
                  dataKey="_id"
                  emptyMessage="No power meters found."
                  sortMode="multiple"
                  paginator
                  rows={rows}
                  rowsPerPageOptions={rowsPerPageOptions}
                  stripedRows
                  loading={loading}
                  tableStyle={{ minWidth: '50rem' }}
                >
                  <Column field="meterSerialNumber" header="Serial Number" sortable />
                  <Column field="deviceId" header="Device ID" sortable />
                  <Column field="gatewayId" header="Gateway ID" sortable />
                  <Column header="Facility" body={facilityTemplate} sortable />
                  <Column header="Manufacturer" body={manufacturerTemplate} sortable />
                  <Column header="Type" body={typeTemplate} sortable />
                  <Column header="Current Reading" body={meterReadingTemplate} sortable />
                  <Column field="liveStatus" header="Status" body={statusTemplate} sortable />
                  <Column header="Meter Live Status" body={liveStatusTemplate} sortable />
                  <Column header="Last Updated" body={lastUpdatedTemplate} sortable />
                  <Column header="Actions" body={actionsTemplate} />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header="Edit Power Meter"
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: '50vw' }}
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label className="form-label">
              Facility<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.facilityId || ''}
              onChange={(e) => setFormData({ ...formData, facilityId: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Facility</option>
              {facilities.map((facility) => (
                <option key={facility._id} value={facility._id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Meter Serial Number<span style={{ color: "red" }}>*</span>
            </label>
            <InputText
              value={formData.meterSerialNumber}
              onChange={(e) => setFormData({ ...formData, meterSerialNumber: e.target.value })}
              className="form-control"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Device ID
            </label>
            <InputText
              value={formData.deviceId}
              onChange={(e) => setFormData({ ...formData, deviceId: e.target.value })}
              className="form-control"
              type="number"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Gateway ID<span style={{ color: "red" }}>*</span>
            </label>
            <InputText
              value={formData.gatewayId}
              onChange={(e) => setFormData({ ...formData, gatewayId: e.target.value })}
              className="form-control"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Manufacturer<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.manufacturer || ''}
              onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Manufacturer</option>
              {manufacturers.map((mfr) => (
                <option key={mfr._id} value={mfr.name}>
                  {mfr.name}
                </option>
              ))}
            </select>
            {manufacturers.length === 0 && (
              <small className="text-muted">
                No manufacturers found. Please add manufacturers in the configuration section.
              </small>
            )}
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Meter Type<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.type || ''}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Type</option>
              {meterTypes.map((t) => (
                <option key={t._id} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Current Reading (kWh)<span style={{ color: "red" }}>*</span>
            </label>
            <InputText
              className="form-control"
              value={formData.meterReading}
              onChange={(e) => setFormData({ ...formData, meterReading: e.target.value })}
              type="number"
              min="0"
              step="0.01"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Status<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.liveStatus || ''}
              onChange={(e) => setFormData({ ...formData, liveStatus: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Status</option>
              {liveStatusOptions.map((s) => (
                <option key={s._id} value={s.name.toLowerCase()}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="text-end mt-4">
          <Button
            label={isSubmitting ? "Saving..." : "Save"}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="p-button-primary"
          />
        </div>
      </Dialog>
      <ImportDialog />
    </Layout>
  );
}

export default PowerMeters;